/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  OverlayPluginPayload,
  PluginsState,
  SetPluginStatePayload,
} from './types';

export const initialState: PluginsState = {
  pluginStates: {},
  isDebugDialogOpen: false,
  overlayPlugins: {},
};

const slice = createSlice({
  initialState,
  name: 'plugins',
  reducers: {
    setGeoWebPluginState: (
      draft: Draft<PluginsState>,
      action: PayloadAction<SetPluginStatePayload>,
    ) => {
      const { panelId, pluginState } = action.payload;
      draft.pluginStates[panelId] = pluginState;
    },
    clearGeoWebPluginState: (
      draft: Draft<PluginsState>,
      action: PayloadAction<{ panelId: string }>,
    ) => {
      const { panelId } = action.payload;
      delete draft.pluginStates[panelId];
    },
    toggleDebugDialog: (
      draft: Draft<PluginsState>,
      action: PayloadAction<{ shouldOpen: boolean }>,
    ) => {
      const { shouldOpen } = action.payload;
      draft.isDebugDialogOpen = shouldOpen;
    },
    showOverlayPlugin: (
      draft: Draft<PluginsState>,
      action: PayloadAction<OverlayPluginPayload>,
    ) => {
      const { panelId, pluginId } = action.payload;
      const overlayPlugins = draft.overlayPlugins[panelId] ?? [];
      const overlayPlugin = overlayPlugins.find(
        (plugin) => plugin.pluginId === pluginId,
      );
      if (overlayPlugin) {
        overlayPlugin.isVisible = true;
      } else {
        overlayPlugins.push({ pluginId, isVisible: true });
      }
      draft.overlayPlugins[panelId] = overlayPlugins;
    },
    hideOverlayPlugin: (
      draft: Draft<PluginsState>,
      action: PayloadAction<OverlayPluginPayload>,
    ) => {
      const { panelId, pluginId } = action.payload;
      const overlayPlugin = draft.overlayPlugins[panelId]?.find(
        (plugin) => plugin.pluginId === pluginId,
      );
      if (overlayPlugin) {
        overlayPlugin.isVisible = false;
      }
    },
    removeOverlayPlugin: (
      draft: Draft<PluginsState>,
      action: PayloadAction<OverlayPluginPayload>,
    ) => {
      const { panelId, pluginId } = action.payload;
      draft.overlayPlugins[panelId] = (
        draft.overlayPlugins[panelId] ?? []
      ).filter((plugin) => plugin.pluginId !== pluginId);
    },
  },
});

export const { reducer: pluginsReducer, actions: pluginsActions } = slice;
